<template>
  <div class="date-range-presets">
    <div class="date-range-presets__modes">
      <label
        class="preset"
        v-for="{ id, label, title } in presets"
        v-bind:key="`${$options.presetRadioName}:${id}`"
        v-bind:class="{ active: selectedPresetId === id }"
        v-bind:title="title">
        <input
          class="preset__input"
          type="radio"
          v-bind:checked="id === selectedPresetId"
          v-bind:id="`${$options.presetRadioName}-${id}`"
          v-bind:name="$options.presetRadioName"
          v-on:input="onInputPreset(id)">
        <span class="preset__label">{{ label }}</span>
      </label>
      <button
        v-if="buttonClearShow"
        class="date-range-presets__button"
        type="button"
        title="Очистить"
        v-on:click="onClickClearDate"
      />
    </div>
  </div>
</template>
<script>
import { convertToInputDate, formatDate } from '@/utils/timeFormat';

const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;

const WEEK_MILLISECONDS = 24 * 60 * 60 * 1000 * 7;

/**
 * @description Предварительные периоды.
 */
const defaultPresets = [
  {
    id: 'tomorrow',
    label: 'Вчера',
    title: 'Показать отчет от вчерашнего дня',
    toValue: () => {
      const to = new Date();
      const from = new Date();
      from.setTime(from.getTime() - DAY_MILLISECONDS);
      to.setTime(to.getTime() - DAY_MILLISECONDS);
      return [from, to];
    },
  },
  {
    id: 'week',
    label: 'Неделя',
    title: 'Показать отчет от прошлой недели',
    toValue: () => {
      const to = new Date();
      const from = new Date();
      from.setTime(from.getTime() - WEEK_MILLISECONDS);
      return [from, to];
    },
  },
  {
    id: 'month',
    label: 'Месяц',
    title: 'Показать отчет от прошлого месяца',
    toValue: () => {
      const to = new Date();
      const currentMonth = to.getMonth();
      const from = new Date();
      from.setMonth(currentMonth - 1);
      return [from, to];
    },
  },
  {
    id: 'year',
    label: 'Год',
    title: 'Показать отчет от прошлого года',
    toValue: () => {
      const to = new Date();
      const currentYear = to.getFullYear();
      const from = new Date();
      from.setFullYear(currentYear - 1);
      return [from, to];
    },
  },
];

/**
 * @description Выбор диапазона дат с предварительными вариантами.
 * @type {Vue}
 */
export default {
  computed: {
    dateTo() {
      const [from, to] = this.datePickerValue;
      return to || from;
    },
    selectedPresetId() {
      const [from, to] = this.datePickerValue.slice().map(convertToInputDate);
      const selectedPreset = this.presets.find(({ toValue }) => {
        const [first, second] = toValue().map(convertToInputDate);
        return first === from && second === to;
      });
      return selectedPreset ? selectedPreset.id : null;
    },
  },
  convertToInputDate,
  data() {
    return {
      datePickerValue: this.value,
    };
  },
  formatDate,
  methods: {
    /**
     * @description Обработать ввод.
     * @param preset
     * @param updatedValue
     * @returns {void}
     */
    onInput(preset, updatedValue) {
      this.$emit('input', updatedValue);
      this.$emit('input-preset', preset);
    },
    /**
     * @description Обработать установку пресета.
     * @param value
     * @returns {void}
     */
    onInputPreset(value) {
      const [updatedValue] = this.presets.flatMap(({ id, toValue }) => {
        if (id === value) {
          return [toValue()];
        }
        return [];
      });
      this.datePickerValue = updatedValue;
      this.onInput(value, updatedValue);
    },
    /**
     * @description Обработать очистку дат.
     * @returns {void}
     */
    onClickClearDate() {
      this.datePickerValue = [];
      this.$emit('click-clear');
    },
  },
  name: 'DateRangePresets',
  presetRadioName: 'date-range-presets-radio',
  props: {
    presets: {
      default: () => defaultPresets,
      required: false,
      type: Array,
    },
    value: {
      required: true,
      type: Array,
    },
    buttonClearShow: {
      default: false,
      type: Boolean,
    },
  },
  watch: {
    value(date) {
      this.datePickerValue = date;
    },
  },
};
</script>
<style lang="scss" scoped>
.date-range-presets {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  &__date {
    &.custom-color {
      color: var(--color-date);
    }
  }
  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px;
    min-width: 40px;
    height: 2.5rem;
    font-size: .875rem;
    font-weight: 400;
    line-height: 1.065rem;
    color: var(--reading-color);
    background: var(--fill-color) url("@/assets/img/cross/cross-grey.svg") no-repeat 50%;
    background-size: 16px 16px;
    cursor: pointer;
    &:hover {
      background-image: url("@/assets/img/cross/cross-white.svg");
      background-color: var(--action-color);
    }
  }
}
.date-range-presets .date-range-presets__date:not(.date-selected),
.date-range-presets .date-range-presets__input {
  opacity: 0;
}
.date-range-presets .date-range-presets__input {
  left: 0;
  position: absolute;
  width: 0;
}
.date-range-presets .date-range-presets__modes {
  align-items: center;
  border-radius: var(--input-border-radius);
  display: flex;
  flex-direction: row;
  overflow: clip;
}
.date-range-presets .date-range-presets__search {
  align-items: center;
  background: var(--primary-background-color);
  border-radius: var(--input-border-radius);
  border: 1px solid var(--border-color);
  display: flex;
  font-size: 1rem;
  font-weight: 400;
  height: 2.5rem;
  line-height: 1.1875rem;
  padding: 0 0 0 .625rem;
  position: relative;
  width: 15.625rem;
}
.date-range-presets .date-range-presets__search:after {
  background-color: var(--fill-color);
  background-image: url("@/assets/img/calendar-grey.svg");
  background-position: center;
  border-radius: 0 4px 4px 0;
  content: '';
  height: 38px;
  overflow: hidden;
  position: absolute;
  right: 0;
  width: 38px;
}
.date-range-presets .date-range-presets__select {
  height: inherit;
  left: 0;
  padding: inherit;
  position: absolute;
  text-align: inherit;
  width: inherit;
  z-index: 1;
}
.preset.active > .preset__label,
.preset > .preset__label:hover {
  background: var(--action-color);
  color: var(--primary-background-color);
}
.preset > .preset__input {
  opacity: 0;
  position: absolute;
  z-index: -1;
}
.preset > .preset__label {
  align-items: center;
  background: var(--fill-color);
  color: var(--reading-color);
  cursor: pointer;
  display: flex;
  font-size: .875rem;
  font-weight: 400;
  height: 2.5rem;
  justify-content: center;
  line-height: 1.065rem;
  padding: 0 .8125rem;
}
@media screen and (max-width: 600px) {
  .date-range-presets {
    flex-wrap: wrap;
  }
  .date-range-presets .date-range-presets__search {
    width: min(100%, 15.625rem);
  }
}
</style>
